import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import usePredictorVotes from "hooks/predictors/usePredictorVotes";
import useAuth from "hooks/auth/useAuth";
import PointsDisplay from "components/card/PointsDisplay";
import { calculatePoints } from "libs/utils";
import LoginDialog from "components/card/LoginDialog";
import VerifyEmailDialog from "components/card/VerifyEmailDialog";
import useLikes from "hooks/predictions/useLikes";
import CommentSection from "components/social/CommentSection";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { collection, getDocs } from "firebase/firestore";
import { db } from "config/firebase";

const PredictorShortsCard = ({ predictor, onVote }) => {
  const router = useRouter();
  const { handleVote } = usePredictorVotes();
  const { currentUser } = useAuth();
  const [currentCard, setCurrentCard] = useState(predictor);

  const [isOpen, setIsOpen] = useState(false);
  const [isOpenVerifyEmailDialog, setIsOpenVerifyEmailDialog] = useState(false);

  useEffect(() => {
    setCurrentCard(predictor);
  }, [predictor]);

  const { likeCount, toggleLike, checkIfLiked } = useLikes(
    `predictors/${predictor.id}`,
  );
  const isLiked = checkIfLiked(currentUser?.uid);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);

  useEffect(() => {
    if (!predictor.id) return;
    const commentsRef = collection(db, "predictors", predictor.id, "comments");
    // Use a one-time fetch for shorts view to reduce listeners and optimize swipe performance
    getDocs(commentsRef).then((snapshot) => {
      setCommentCount(snapshot.size);
    });
  }, [predictor.id]);

  if (!currentCard) return null;

  const { agreeCount, disagreeCount } = currentCard.votes
    ? calculatePoints(currentCard.votes)
    : { agreeCount: 0, disagreeCount: 0 };

  const userVote = currentCard.votes?.[currentUser?.uid] || "";

  const toggleVote = (voteType) => {
    if (!currentUser) {
      setIsOpen(true);
      return;
    }
    if (currentUser && !currentUser.emailVerified) {
      if (currentUser.providerData?.[0].providerId !== "facebook.com") {
        setIsOpenVerifyEmailDialog(true);
        return;
      }
    }
    const newVote = userVote === voteType ? "" : voteType;
    handleVote(currentCard.id, currentUser?.uid, newVote);
    setCurrentCard((prev) => ({
      ...prev,
      votes: {
        ...prev.votes,
        [currentUser?.uid]: newVote,
      },
    }));
    if (onVote && newVote !== "") {
      onVote();
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/predictors/${currentCard.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${currentCard.firstName} ${currentCard.lastName}`,
          text: currentCard.description,
          url: url,
        });
      } catch (err) {
        console.log("Share failed:", err);
      }
    } else {
      navigator.clipboard.writeText(url);
      alert("Հղումը պատճենվեց:"); // Link copied
    }
  };

  return (
    <div className="relative w-full h-[100%] max-h-[100%] aspect-[9/16] bg-[#030014] md:rounded-[24px] rounded-none overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.8)] border-0 md:border border-cyan-500/20 group shorts-container">
      {/* Background Media */}
      <div className="absolute inset-0 z-0">
        <Image
          src={currentCard.image || "/default_video.webp"}
          alt={`${currentCard.firstName} ${currentCard.lastName}`}
          fill
          className="object-cover transition-transform duration-1000 group-hover:scale-105"
          sizes="(max-width: 768px) 100vw, 450px"
          quality={75}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent h-1/3" />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent h-2/3" />
      </div>

      {/* Floating Action Buttons (Right Side) */}
      <div
        className="absolute right-2 md:right-4 bottom-[25%] md:bottom-[20%] flex flex-col items-center gap-[24px] z-30"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* Agree Button */}
        <div
          className="flex flex-col items-center gap-1 group/btn cursor-pointer"
          onClick={() => toggleVote("agree")}
        >
          <div
            className={`p-3 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-300 ${userVote === "agree" ? "bg-cyan-500 text-white shadow-[0_0_20px_rgba(102,133,255,0.5)]" : "bg-black/40 text-white hover:bg-black/60"}`}
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              width="28"
              height="28"
              className="drop-shadow-lg text-white"
            >
              <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
            </svg>
          </div>
          <span className="text-white text-[12px] font-bold drop-shadow-md">
            {agreeCount}
          </span>
        </div>

        {/* Disagree Button */}
        <div
          className="flex flex-col items-center gap-1 group/btn cursor-pointer"
          onClick={() => toggleVote("disagree")}
        >
          <div
            className={`p-3 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-300 ${userVote === "disagree" ? "bg-red-500 text-white shadow-[0_0_20px_rgba(239,68,68,0.5)]" : "bg-black/40 text-white hover:bg-black/60"}`}
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              width="28"
              height="28"
              className="drop-shadow-lg text-white"
            >
              <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" />
            </svg>
          </div>
          <span className="text-white text-[12px] font-bold drop-shadow-md">
            {disagreeCount}
          </span>
        </div>

        {/* Share Button */}
        <div
          className="flex flex-col items-center gap-1 group/btn cursor-pointer"
          onClick={handleShare}
        >
          <div className="p-3 rounded-full flex items-center justify-center bg-black/40 text-white backdrop-blur-md hover:bg-black/60 transition-all duration-300">
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              width="28"
              height="28"
              className="drop-shadow-lg text-white"
            >
              <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
            </svg>
          </div>
          <span className="text-white text-[12px] font-bold drop-shadow-md">
            Կիսվել
          </span>
        </div>

        {/* Predictor Like Button */}
        <div
          className="flex flex-col items-center gap-1 group/btn cursor-pointer"
          onClick={() => toggleLike(currentUser?.uid)}
        >
          <div
            className={`p-3 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-300 ${isLiked ? "bg-rose-500 text-white shadow-[0_0_20px_rgba(244,63,94,0.5)]" : "bg-black/40 text-white hover:bg-black/60"}`}
          >
            {isLiked ? (
              <FavoriteIcon sx={{ fontSize: 28 }} />
            ) : (
              <FavoriteBorderIcon sx={{ fontSize: 28 }} />
            )}
          </div>
          <span className="text-white text-[12px] font-bold drop-shadow-md">
            {likeCount || 0}
          </span>
        </div>

        {/* Comment Toggle Button */}
        <div
          className="flex flex-col items-center gap-1 group/btn cursor-pointer"
          onClick={() => setShowComments(true)}
        >
          <div className="p-3 rounded-full flex items-center justify-center bg-black/40 text-white backdrop-blur-md hover:bg-black/60 transition-all duration-300">
            <ChatBubbleOutlineIcon sx={{ fontSize: 28 }} />
          </div>
          <span className="text-white text-[12px] font-bold drop-shadow-md">
            {commentCount || 0}
          </span>
        </div>
      </div>

      {/* Comment Overlay for Shorts */}
      {showComments && (
        <div className="absolute inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowComments(false)}
          />
          <div className="relative h-full bg-[#030014]/95 light:bg-white border-t border-cyan-500/30 light:border-slate-200 flex flex-col animate-in slide-in-from-bottom duration-300 overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-cyan-500/10 light:border-slate-100">
              <h3 className="text-white light:text-slate-900 font-bold tracking-wider">
                Մեկնաբանություններ
              </h3>
              <button
                onClick={() => setShowComments(false)}
                className="p-1 rounded-full hover:bg-white/10 light:hover:bg-black/5 text-white/70 light:text-slate-500"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <CommentSection
                docPath={`predictors/${predictor.id}`}
                isInModal={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Metadata (Bottom Left) */}
      <div
        className="absolute bottom-[100px] md:bottom-6 left-4 right-20 flex flex-col items-start gap-[12px] z-20 pointer-events-none"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* Points Stats */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-emerald-500/80 backdrop-blur-sm border border-emerald-400 text-white rounded-lg px-2 py-1 shadow-[0_0_10px_rgba(16,185,129,0.2)]">
            <span className="text-[10px] md:text-[12px]  tracking-widest uppercase">
              Ճիշտ
            </span>
            <span className="font-bold text-[12px] md:text-[14px]">
              {currentCard.collectedPoints}
            </span>
          </div>
          <div className="flex items-center gap-2 bg-red-500/80 backdrop-blur-sm border border-red-500 text-white rounded-lg px-2 py-1 shadow-[0_0_10px_rgba(239,68,68,0.2)]">
            <span className="text-[10px] md:text-[12px]  tracking-widest uppercase">
              Սխալ
            </span>
            <span className="font-bold text-[12px] md:text-[14px]">
              {currentCard.lostPoints}
            </span>
          </div>
        </div>

        {/* Title / Name */}
        <h2
          onClick={() => router.push(`/predictors/${currentCard.id}`)}
          className="text-white text-[20px] md:text-[26px] font-black leading-tight drop-shadow-lg cursor-pointer pointer-events-auto hover:text-cyan-300 transition-colors line-clamp-2 uppercase tracking-tight"
        >
          {currentCard.firstName} {currentCard.lastName}
        </h2>

        {/* Description / Bio */}
        <div className="bg-black/30 backdrop-blur-md rounded-xl p-3 border border-white/10 pointer-events-auto">
          <p className="text-white/90 text-[13px] md:text-[15px] leading-relaxed line-clamp-4">
            {currentCard.description}
          </p>
        </div>

        {/* View Buttons (History, Predictions, Promises) */}
        <div className="flex flex-wrap gap-2 mt-2 pointer-events-auto">
          <button
            onClick={() => router.push(`/predictors/${currentCard.id}`)}
            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white text-[12px] font-bold transition-all"
          >
            Պատմություն
          </button>
          {currentCard.predictionsCount > 0 && (
            <button
              onClick={() =>
                router.push({
                  pathname: "/predictions",
                  query: { predictor: currentCard.id },
                })
              }
              className="px-3 py-1.5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 text-white text-[12px] font-bold transition-all"
            >
              Կանխատեսումներ ({currentCard.predictionsCount})
            </button>
          )}
          {currentCard.promisesCount > 0 && (
            <button
              onClick={() =>
                router.push({
                  pathname: "/promises",
                  query: { predictor: currentCard.id },
                })
              }
              className="px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-white text-[12px] font-bold transition-all"
            >
              Խոստումներ ({currentCard.promisesCount})
            </button>
          )}
        </div>
      </div>

      <LoginDialog isOpen={isOpen} handleClose={() => setIsOpen(false)} />
      <VerifyEmailDialog
        isOpen={isOpenVerifyEmailDialog}
        handleClose={() => setIsOpenVerifyEmailDialog(false)}
      />
    </div>
  );
};

export default PredictorShortsCard;
